import { useMemo } from "react";
import { Box, type SxProps } from "@mui/material";
import { useTheme, type Theme } from "@mui/material/styles";
import { DataGrid, type GridValidRowModel } from "@mui/x-data-grid";
import { t } from "i18next";
import { renderColumns } from "./columns";
import type { BaseTableProps } from "./CustomDataTable.types";
import { dataGridSx, dataGridWrapperSx } from "./styles";

type BaseTableExtra<T> = {
  onRowEdit?: (row: T) => void;
  onRowDelete?: (row: T) => void;
  onRowClick?: (row: T, e: any) => void;
  customSx?: SxProps<Theme>;
};

const BaseTable = <T extends GridValidRowModel>({
  rows,
  headers,
  customSx,
  onRowClick,
  onRowEdit,
  onRowDelete,
}: BaseTableProps<T> & BaseTableExtra<T>) => {
  const theme = useTheme();

  const columns = useMemo(
    () =>
      renderColumns(headers, rows, theme, undefined, { onEdit: onRowEdit, onDelete: onRowDelete }),
    [headers, rows, theme, onRowEdit, onRowDelete, onRowClick],
  );
  const sx = (customSx ? [dataGridSx, customSx] : dataGridSx) as SxProps<Theme>;

  return (
    <Box sx={dataGridWrapperSx}>
      <DataGrid
        rows={rows}
        columns={columns}
        hideFooter
        disableColumnMenu
        disableColumnSorting
        disableColumnResize
        localeText={{ noRowsLabel: t("common.empty-table.no-rows") }}
        onRowClick={(params, e) => {
          if (onRowClick) {
            onRowClick(params.row, e);
          }
        }}
        getRowId={(row) => row.id}
        sx={sx}
      />
    </Box>
  );
};

export default BaseTable;
